import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import styles from './PhoneInput.module.css';
import { withFloatingLabel } from '../FloatingLabel/withFloatingLabel';
import { countries } from './countryData.js';

// Windows ships no flag emoji glyphs, so flags render as bare letters ("US").
// The polyfill registers a flags-only web font, and only on browsers that lack them.
polyfillCountryFlagEmojis();

// Caret index right after the nth digit of a formatted value (before the first digit when n is 0)
const getCaretAfterDigits = (formatted, digitCount) => {
  if (digitCount <= 0) {
    const firstDigit = formatted.search(/\d/);
    return firstDigit === -1 ? formatted.length : firstDigit;
  }
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      seen++;
      if (seen === digitCount) {
        return i + 1;
      }
    }
  }
  return formatted.length;
};

// Helper to clean non-digit characters (keep leading + if present)
const cleanDigits = (val) => {
  if (!val) {
    return '';
  }
  return val.replace(/\D/g, '');
};

// Sort countries by dialCode length descending to match longest dialCode first
const sortedCountries = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length);

const getCountryByDigits = (digits, fallbackIso, disableAreaCodes) => {
  const filtered = disableAreaCodes 
    ? sortedCountries.filter(c => !c.isAreaCode)
    : sortedCountries;

  const matches = filtered.filter(c => digits.startsWith(c.dialCode));
  if (matches.length > 0) {
    const longestLen = matches[0].dialCode.length;
    const candidates = matches.filter(c => c.dialCode.length === longestLen);
    if (fallbackIso) {
      const preferred = candidates.find(c => c.iso2.toLowerCase() === fallbackIso.toLowerCase());
      if (preferred) {
        return preferred;
      }
    }
    return candidates[0];
  }

  // Fallback to the country matching fallbackIso
  const fallback = countries.find(c => c.iso2.toLowerCase() === fallbackIso.toLowerCase() && !c.isAreaCode);
  return fallback || countries[0];
};

const formatPhone = (digits, country) => {
  if (!country) {
    return '+' + digits;
  }

  // If the typed digits are a prefix of the country's dial code, format as just +digits (allows digit-by-digit deletion)
  if (country.dialCode.startsWith(digits)) {
    return digits ? '+' + digits : '';
  }
  
  if (!country.format) {
    const dial = country.dialCode;
    if (digits.startsWith(dial)) {
      const rest = digits.slice(dial.length);
      return `+${dial}${rest ? ' ' + rest : ''}`;
    }
    return `+${country.dialCode}${digits ? ' ' + digits : ''}`;
  }

  let formatted = '';
  let digitIdx = 0;
  const pattern = country.format;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    if (char === '.') {
      if (digitIdx < digits.length) {
        formatted += digits[digitIdx++];
      } else {
        break;
      }
    } else {
      if (digitIdx < digits.length) {
        formatted += char;
      } else {
        break;
      }
    }
  }

  // Append remaining digits if any
  if (digitIdx < digits.length) {
    formatted += digits.slice(digitIdx);
  }

  return formatted;
};

const getBrowserCountry = () => {
  try {
    if (typeof navigator !== 'undefined' && navigator.languages) {
      for (const lang of navigator.languages) {
        if (lang.includes('-')) {
          const region = lang.split('-')[1].toLowerCase();
          if (region.length === 2) {
            return region;
          }
        }
      }
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      const parts = navigator.language.split('-');
      if (parts.length > 1) {
        const region = parts[parts.length - 1].toLowerCase();
        if (region.length === 2) {
          return region;
        }
      }
    }
  } catch (e) {
    // Ignore
  }
  return null;
};

/**
 * PhoneInput component styled with CSS Modules and replicating material-ui-phone-number props.
 */
const PhoneInput = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  defaultCountry = 'us',
  autoDetect = false,
  label,
  error,
  disabled = false,
  fullWidth = false,
  placeholder = 'Phone Number',
  disableAreaCodes = false,
  menuPortal = true,
  className,
  style,
  floatingLabel = false,
  isFloating = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPos, setMenuPos] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  // Digits that should sit left of the caret once the re-formatted value renders
  const pendingCaretRef = useRef(null);

  const resolvedDefaultCountry = useMemo(() => {
    if (autoDetect) {
      const browserCountry = getBrowserCountry();
      if (browserCountry) {
        const exists = countries.some(c => c.iso2.toLowerCase() === browserCountry.toLowerCase());
        if (exists) {
          return browserCountry;
        }
      }
    }
    return defaultCountry;
  }, [autoDetect, defaultCountry]);

  // Parse current state based on props value
  const { currentDigits, currentCountry } = useMemo(() => {
    let cleaned = cleanDigits(value);
    const country = getCountryByDigits(cleaned, resolvedDefaultCountry, disableAreaCodes);
    if (country && cleaned.startsWith(country.dialCode + '0')) {
      cleaned = country.dialCode + cleaned.slice(country.dialCode.length + 1);
    }
    return {
      currentDigits: cleaned,
      currentCountry: country
    };
  }, [value, resolvedDefaultCountry, disableAreaCodes]);

  // If value is empty, we can display empty input or default country prefix when focused
  const displayValue = useMemo(() => {
    if (!value) {
      return '';
    }
    return formatPhone(currentDigits, currentCountry);
  }, [value, currentDigits, currentCountry]);

  // Determine portal target DOM element
  const portalTarget = useMemo(() => {
    if (!menuPortal || typeof document === 'undefined') {
      return null;
    }
    return (containerRef.current && containerRef.current.closest('dialog')) || document.body;
  }, [menuPortal, isOpen]);

  // Position portal dropdown relative to input field container
  const updateMenuPos = () => {
    const anchor = containerRef.current;
    if (!anchor) {
      return;
    }
    const r = anchor.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const menuHeight = 250;
    const spaceBelow = viewportHeight - r.bottom - 10;
    const spaceAbove = r.top - 10;

    let clientTop = r.bottom;
    let clientLeft = r.left;
    let clientWidth = r.width;
    let clientMaxHeight = 250;

    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      clientTop = r.top - Math.min(menuHeight, spaceAbove);
      clientMaxHeight = spaceAbove;
    } else {
      clientMaxHeight = spaceBelow;
    }

    setMenuPos({
      top: clientTop,
      left: clientLeft,
      width: clientWidth,
      maxHeight: Math.min(250, clientMaxHeight),
    });
  };

  // Position updating event listeners for portal mode
  useEffect(() => {
    if (!menuPortal || !isOpen) {
      return undefined;
    }

    updateMenuPos();
    const rafId = requestAnimationFrame(() => {
      updateMenuPos();
    });

    const handleScroll = () => {
      setIsScrolling(true);
      updateMenuPos();

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    const handleResize = () => updateMenuPos();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [menuPortal, isOpen]);

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownClick = dropdownRef.current && dropdownRef.current.contains(event.target);
      const isContainerClick = containerRef.current && containerRef.current.contains(event.target);
      if (!isDropdownClick && !isContainerClick) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Reset search query and menu position when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setMenuPos(null);
    }
  }, [isOpen]);

  // Filter countries list based on search query
  const filteredCountriesList = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const baseList = disableAreaCodes ? countries.filter(c => !c.isAreaCode) : countries;
    if (!query) {
      return baseList;
    }
    return baseList.filter(c => 
      c.name.toLowerCase().includes(query) || 
      c.dialCode.includes(query) ||
      c.iso2.toLowerCase().includes(query)
    );
  }, [searchQuery, disableAreaCodes]);

  const moveCaret = (formatted, digitCount) => {
    const input = inputRef.current;
    if (!input || document.activeElement !== input) {
      return;
    }
    const pos = getCaretAfterDigits(formatted, digitCount);
    input.setSelectionRange(pos, pos);
  };

  // Re-formatting rewrites the whole value, which drops the caret at the end;
  // put it back after the same digit it followed before the edit
  useLayoutEffect(() => {
    if (pendingCaretRef.current === null) {
      return;
    }
    moveCaret(displayValue, pendingCaretRef.current);
    pendingCaretRef.current = null;
  });

  const applyRawValue = (rawVal, caretPos) => {
    let cleaned = cleanDigits(rawVal);
    let digitsBeforeCaret = cleanDigits(rawVal.slice(0, caretPos)).length;

    // Find country based on newly typed digits
    const newCountry = getCountryByDigits(cleaned, currentCountry.iso2, disableAreaCodes);

    // Strip leading zero right after the country dial code
    if (newCountry && cleaned.startsWith(newCountry.dialCode + '0')) {
      cleaned = newCountry.dialCode + cleaned.slice(newCountry.dialCode.length + 1);
      if (digitsBeforeCaret > newCountry.dialCode.length) {
        digitsBeforeCaret--;
      }
    }

    const formatted = formatPhone(cleaned, newCountry);
    pendingCaretRef.current = digitsBeforeCaret;
    onChange?.(formatted);

    // An unchanged value (e.g. a stripped letter) never re-renders, so the layout effect won't run
    if (formatted === displayValue) {
      requestAnimationFrame(() => {
        moveCaret(formatted, digitsBeforeCaret);
        pendingCaretRef.current = null;
      });
    }
  };

  const handleInputChange = (e) => {
    if (disabled) {
      return;
    }
    applyRawValue(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  // Deleting a separator ("(", ")", "-", space) changes no digit, so the formatter puts it
  // right back and the key looks dead; delete the nearest digit in that direction instead
  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (disabled || e.defaultPrevented || (e.key !== 'Backspace' && e.key !== 'Delete')) {
      return;
    }
    const { selectionStart, selectionEnd } = e.target;
    if (selectionStart !== selectionEnd) {
      return;
    }
    const isBackspace = e.key === 'Backspace';
    const deletedIdx = isBackspace ? selectionStart - 1 : selectionStart;
    if (deletedIdx < 0 || deletedIdx >= displayValue.length || /\d/.test(displayValue[deletedIdx])) {
      return;
    }
    e.preventDefault();

    let digitIdx = deletedIdx;
    while (digitIdx >= 0 && digitIdx < displayValue.length && !/\d/.test(displayValue[digitIdx])) {
      digitIdx += isBackspace ? -1 : 1;
    }
    if (digitIdx < 0 || digitIdx >= displayValue.length) {
      return;
    }
    const rawVal = displayValue.slice(0, digitIdx) + displayValue.slice(digitIdx + 1);
    applyRawValue(rawVal, isBackspace ? digitIdx : selectionStart);
  };

  const handleCountrySelect = (country) => {
    if (disabled) {
      return;
    }
    
    // Set value to just the dial code of selected country
    const formatted = formatPhone(country.dialCode, country);
    onChange?.(formatted);
    setIsOpen(false);
    
    // Focus the input field after selection
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleFocus = (e) => {
    // If the input is focused and completely empty, pre-fill with country dial code
    if (!value && currentCountry) {
      const formatted = formatPhone(currentCountry.dialCode, currentCountry);
      onChange?.(formatted);
    }
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    onBlur?.(e);
  };

  // Build classnames dynamically
  const containerClassNames = [
    styles.phoneInputContainer,
    fullWidth && styles.phoneInputContainerFull,
    className
  ].filter(Boolean).join(' ');

  const labelClassNames = [
    styles.phoneInputLabel,
    error && styles.phoneInputLabelError
  ].filter(Boolean).join(' ');

  const inputClassNames = [
    styles.phoneInput,
    error && styles.phoneInputError,
    floatingLabel && styles.inputWithFloatingLabel
  ].filter(Boolean).join(' ');

  const isErrorText = typeof error === 'string' ? error : null;

  // Build dropdown styles dynamically depending on portal configuration
  const dropdownStyle = useMemo(() => {
    if (menuPortal && menuPos) {
      return {
        position: 'fixed',
        top: `${menuPos.top + 4}px`,
        left: `${menuPos.left}px`,
        width: `${Math.max(300, menuPos.width)}px`,
        maxHeight: `${menuPos.maxHeight}px`,
        margin: 0,
        zIndex: 9999,
      };
    }
    return undefined;
  }, [menuPortal, menuPos]);

  const dropdownNode = isOpen && (!menuPortal || menuPos) && (
    <div ref={dropdownRef} className={styles.dropdownMenu} style={dropdownStyle}>
      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search country..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
      </div>
      <ul className={styles.countryList} role="listbox">
        {filteredCountriesList.map((country, idx) => {
          const isSelected = currentCountry?.iso2 === country.iso2 && currentCountry?.dialCode === country.dialCode;
          const key = `${country.iso2}-${country.dialCode}-${country.isAreaCode ? 'area-' + idx : 'main'}`;
          return (
            <li
              key={key}
              className={`${styles.countryItem} ${isSelected ? styles.countryItemSelected : ''}`}
              onClick={() => handleCountrySelect(country)}
              role="option"
              aria-selected={isSelected}
            >
              <span className={styles.flagEmoji}>{country.flag}</span>
              <span className={styles.countryName}>{country.name}</span>
              <span className={styles.dialCode}>+{country.dialCode}</span>
            </li>
          );
        })}
        {filteredCountriesList.length === 0 && (
          <li className={styles.countryItem} style={{ cursor: 'default', color: '#aaa2aa' }}>
            No countries found
          </li>
        )}
      </ul>
    </div>
  );

  const phoneInputWrapperMarkup = (
    <div ref={floatingLabel ? containerRef : null} className={styles.phoneInputWrapper}>
      <button
        type="button"
        className={styles.flagTrigger}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.flagEmoji}>
          {value || resolvedDefaultCountry ? (currentCountry?.flag || '🏳️') : ''}
        </span>
        <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`} />
      </button>

      <input
        ref={inputRef}
        type="tel"
        className={inputClassNames}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />

      {menuPortal && portalTarget ? createPortal(dropdownNode, portalTarget) : dropdownNode}
    </div>
  );

  if (floatingLabel) {
    return phoneInputWrapperMarkup;
  }

  return (
    <div ref={floatingLabel ? null : containerRef} className={containerClassNames} style={style}>
      {label && <label className={labelClassNames}>{label}</label>}
      {phoneInputWrapperMarkup}
      {isErrorText && (
        <span className={styles.phoneInputErrorText}>{isErrorText}</span>
      )}
    </div>
  );
};

PhoneInput.floatingLabelLeft = '52px';

export default withFloatingLabel(PhoneInput);
